// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// backendConfig — the Convex experiment switches (#191).
//
// Mirrors the Realm->TanStack `conversion` model, but for the Mongo-vs-Convex
// axis and gated PER USER (not by a build-time flag):
//
//   Users.backendMode = "mongoOnly" (default) | "convexOnly" | "both"
//     mongoOnly  — today's path: Lambda + MongoDB + TanStack polling. UNCHANGED.
//     convexOnly — reads/writes go to Convex (live websocket); pollers off.
//     both       — run BOTH, compare results, log parity (Axiom + Convex table).
//
// Convex is only ever *available* when a deployment URL is configured. When it
// isn't (e.g. test env, or a build without the var), everyone is forced to
// mongoOnly and no websocket is opened — the production path is untouched.
//
// REACT_APP_BACKEND is an optional GLOBAL kill switch: set it to "mongo" to
// force every user to mongoOnly regardless of their per-user setting.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const CONVEX_URL = process.env.REACT_APP_CONVEX_URL || "";

// A→B (#388): the PROD Convex box (bright-bear-880). Under model B each box is
// single-source; PROD data + all real history live ONLY here. Time Machine
// (prod-only, always) and Compass's PROD side read this box regardless of which
// env the app itself points at. Overridable via env; defaults to the PROD box.
export const PROD_CONVEX_URL =
  process.env.REACT_APP_PROD_CONVEX_URL || "https://bright-bear-880.convex.cloud";

// The UAT Convex box (bright-mongoose-490). Compass browses BOTH boxes by URL
// (the operator's uat/prod choice), so it needs an explicit UAT-box URL too.
export const UAT_CONVEX_URL =
  process.env.REACT_APP_UAT_CONVEX_URL || "https://bright-mongoose-490.convex.cloud";

// A→B (#388): map Compass's env selector to the box URL (each box single-source).
export const convexUrlForSource = (source) =>
  source === "prod" ? PROD_CONVEX_URL : UAT_CONVEX_URL;

// Convex is wired up (provider mounted, per-user gating live) only when a URL
// is present. No URL -> the whole experiment is dormant.
export const CONVEX_CONFIGURED = CONVEX_URL.length > 0;

// Optional global kill switch.
export const CONVEX_GLOBALLY_DISABLED =
  (process.env.REACT_APP_BACKEND || "").toLowerCase() === "mongo";

// Which mirrored dataset to read ("uat" | "prod"), matching the Mongo DB this
// build would otherwise talk to.
export const CONVEX_SOURCE =
  process.env.REACT_APP_MONGODB_DATABASE === "Warehouses" ? "prod" : "uat";

const VALID_MODES = new Set(["mongoOnly", "convexOnly", "both"]);

/**
 * Resolve the effective backend mode for a user document.
 * Defaults to "mongoOnly" (current behavior) when unset, invalid, when Convex
 * isn't configured, or when globally disabled — so the safe path is the default
 * in every ambiguous case.
 *
 * @param {object|null|undefined} userDocument the user's Mongo doc (tsUserDocument)
 * @returns {"mongoOnly"|"convexOnly"|"both"}
 */
export function resolveBackendMode(userDocument) {
  if (!CONVEX_CONFIGURED || CONVEX_GLOBALLY_DISABLED) return "mongoOnly";
  const mode = userDocument?.backendMode;
  return VALID_MODES.has(mode) ? mode : "mongoOnly";
}

export const usesConvexReads = (mode) =>
  mode === "convexOnly" || mode === "both";
export const usesConvexWrites = (mode) =>
  mode === "convexOnly" || mode === "both";
export const comparesBackends = (mode) => mode === "both";
